import React, { useMemo } from 'react';
import { FaBolt, FaSyncAlt } from 'react-icons/fa';

/**
 * Lightweight parser that turns a free-form AI explanation into 3–5
 * numbered "insights" — each insight has a short title and a description.
 *
 * Heuristics, in order of preference:
 *   1. Sections separated by Markdown ##/### headings.
 *   2. Numbered list items ("1.", "2.", …).
 *   3. Paragraph splits on blank lines.
 *
 * Always caps results at 5 to match the reference card density and
 * never returns empty insights.
 */
const stripHtml = (s) => String(s || '').replace(/<[^>]*>/g, ' ');
const stripMd = (s) => String(s || '')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\*([^*]+)\*/g, '$1');

const parseInsights = (explanation) => {
  if (!explanation || typeof explanation !== 'string') return [];
  const cleaned = stripHtml(explanation).replace(/\r\n/g, '\n').trim();
  if (!cleaned) return [];

  // 1. Markdown headings
  const headingMatches = [
    ...cleaned.matchAll(/^\s*#{1,4}\s+(.+)$/gm)
  ];
  if (headingMatches.length >= 2) {
    const insights = [];
    for (let i = 0; i < headingMatches.length && insights.length < 5; i++) {
      const start = headingMatches[i].index + headingMatches[i][0].length;
      const end = i + 1 < headingMatches.length
        ? headingMatches[i + 1].index
        : cleaned.length;
      const title = stripMd(headingMatches[i][1]).trim();
      const body = stripMd(cleaned.slice(start, end))
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .join(' ');
      if (title) insights.push({ title, description: body });
    }
    if (insights.length > 0) return insights;
  }

  // 2. Numbered list ("1." / "1)" / "1:")
  const numberedRegex = /^\s*(\d{1,2})[.)\]:]\s+(.*)$/gm;
  const numbered = [...cleaned.matchAll(numberedRegex)];
  if (numbered.length >= 2) {
    const insights = [];
    for (let i = 0; i < numbered.length && insights.length < 5; i++) {
      const start = numbered[i].index + numbered[i][0].length;
      const end = i + 1 < numbered.length ? numbered[i + 1].index : cleaned.length;
      const head = stripMd(numbered[i][2]).trim();
      const tail = stripMd(cleaned.slice(start, end))
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .join(' ');
      const text = (head + ' ' + tail).trim();
      const splitIdx = text.search(/[.!?]\s/);
      const title = splitIdx > 0 && splitIdx < 90
        ? text.slice(0, splitIdx + 1).trim()
        : text.slice(0, 70).trim();
      const description = splitIdx > 0 && splitIdx < 90
        ? text.slice(splitIdx + 1).trim()
        : text.slice(70).trim();
      if (title) insights.push({ title, description });
    }
    if (insights.length > 0) return insights;
  }

  // 3. Paragraph fallback — split on blank lines, limit to 5.
  const paragraphs = cleaned
    .split(/\n\s*\n/)
    .map((p) => stripMd(p).trim())
    .filter((p) => p.length >= 12)
    .slice(0, 5);

  return paragraphs.map((p) => {
    const splitIdx = p.search(/[.!?]\s/);
    if (splitIdx > 0 && splitIdx < 90) {
      return {
        title: p.slice(0, splitIdx + 1).trim(),
        description: p.slice(splitIdx + 1).trim()
      };
    }
    return {
      title: p.slice(0, 70).trim(),
      description: p.slice(70).trim()
    };
  });
};

/**
 * ByteInsights
 * Numbered key-takeaways list. When no explanation is available, renders
 * a CTA that asks the parent to generate insights for the current card.
 * Shows a pulsing skeleton while loading.
 */
const ByteInsights = ({
  explanation,
  isLoading = false,
  hasError = false,
  onGenerate,
  onRegenerate,
  generateDisabled = false,
  emptyTitle = 'Get a quick read on this snippet',
  emptySubtitle = 'Byte will pull out the 3 key things to remember.'
}) => {
  const insights = useMemo(() => parseInsights(explanation), [explanation]);

  const RegenerateButton = onRegenerate ? (
    <button
      type="button"
      className="byte-companion__regenerate-btn"
      onClick={onRegenerate}
      disabled={generateDisabled}
      title="Regenerate insights"
      aria-label="Regenerate insights"
    >
      <FaSyncAlt aria-hidden="true" />
      <span>Regenerate</span>
    </button>
  ) : null;

  if (isLoading) {
    return (
      <div className="byte-companion__insights" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="byte-companion__insight byte-companion__insight--skeleton">
            <div className="byte-companion__insight-num" aria-hidden>
              {i + 1}
            </div>
            <div className="byte-companion__insight-body">
              <div className="byte-companion__insight-skeleton-line byte-companion__insight-skeleton-line--title" />
              <div className="byte-companion__insight-skeleton-line" />
              <div className="byte-companion__insight-skeleton-line byte-companion__insight-skeleton-line--short" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!explanation && !hasError) {
    return (
      <div className="byte-companion__empty">
        <div className="byte-companion__empty-icon" aria-hidden>
          <FaBolt />
        </div>
        <h4 className="byte-companion__empty-title">{emptyTitle}</h4>
        <p className="byte-companion__empty-sub">{emptySubtitle}</p>
        <button
          type="button"
          className="byte-companion__generate-btn"
          onClick={onGenerate}
          disabled={generateDisabled}
        >
          Generate Insights
        </button>
      </div>
    );
  }

  if (hasError && insights.length === 0) {
    return (
      <div className="byte-companion__empty byte-companion__empty--error">
        <h4 className="byte-companion__empty-title">Couldn't generate insights</h4>
        <p className="byte-companion__empty-sub">
          Something went wrong reaching Byte. Try again in a moment.
        </p>
        <button
          type="button"
          className="byte-companion__generate-btn"
          onClick={onGenerate}
          disabled={generateDisabled}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (insights.length === 0) {
    // We have an explanation but couldn't parse — show as a single block.
    return (
      <div className="byte-companion__insights">
        <div className="byte-companion__insight">
          <div className="byte-companion__insight-num">1</div>
          <div className="byte-companion__insight-body">
            <div className="byte-companion__insight-desc">
              {stripMd(stripHtml(explanation))}
            </div>
          </div>
        </div>
        {RegenerateButton && (
          <div className="byte-companion__insights-footer">
            {RegenerateButton}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="byte-companion__insights">
      {insights.map((insight, idx) => (
        <div key={idx} className="byte-companion__insight">
          <div className="byte-companion__insight-num">{idx + 1}</div>
          <div className="byte-companion__insight-body">
            <div className="byte-companion__insight-title">{insight.title}</div>
            {insight.description && (
              <div className="byte-companion__insight-desc">
                {insight.description}
              </div>
            )}
          </div>
        </div>
      ))}
      {RegenerateButton && (
        <div className="byte-companion__insights-footer">
          {RegenerateButton}
        </div>
      )}
    </div>
  );
};

export default ByteInsights;
